"use client";

import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";

const DISMISS_KEY = "pwa-install-dismissed-until";
const DISMISS_DURATION_MS = 1000 * 60 * 60 * 24; // 24 hours

interface PWAInstallButtonProps {
    className?: string;
    variant?: "navbar" | "floating";
}

export default function PWAInstallButton({ className, variant = "navbar" }: PWAInstallButtonProps) {
    const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
    const [showModal, setShowModal] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [isStandalone, setIsStandalone] = useState(false);

    const shouldSuppressPrompt = useCallback(() => {
        if (typeof window === "undefined") return false;
        const storedValue = window.localStorage.getItem(DISMISS_KEY);
        if (!storedValue) return false;

        const dismissedUntil = Number(storedValue);
        if (!Number.isFinite(dismissedUntil)) {
            window.localStorage.removeItem(DISMISS_KEY);
            return false;
        }

        const isStillSuppressed = dismissedUntil > Date.now();
        if (!isStillSuppressed) {
            window.localStorage.removeItem(DISMISS_KEY);
        }
        return isStillSuppressed;
    }, []);

    useEffect(() => {
        // Check if already installed as PWA
        if (typeof window !== "undefined") {
            const standalone = window.matchMedia("(display-mode: standalone)").matches;
            setIsStandalone(standalone);

            // Check for iOS
            const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
            setIsIOS(iOS);
        }

        const handleBeforeInstallPrompt = (e: any) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };

        window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
        return () =>
            window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    }, []);

    const handleInstallClick = async () => {
        if (deferredPrompt) {
            // Chrome/Edge - use native prompt
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            if (outcome !== "accepted") {
                if (typeof window !== "undefined") {
                    window.localStorage.setItem(
                        DISMISS_KEY,
                        String(Date.now() + DISMISS_DURATION_MS)
                    );
                }
            }
            setDeferredPrompt(null);
        } else {
            // Show modal with instructions for iOS/Safari
            setShowModal(true);
        }
    };

    const handleDismissModal = () => {
        setShowModal(false);
        if (typeof window !== "undefined") {
            window.localStorage.setItem(
                DISMISS_KEY,
                String(Date.now() + DISMISS_DURATION_MS)
            );
        }
    };

    // Don't show if already installed as PWA
    if (isStandalone) return null;

    // Don't show if user dismissed recently (only for floating variant)
    if (variant === "floating" && shouldSuppressPrompt()) return null;

    return (
        <>
            <Button
                type="button"
                onClick={handleInstallClick}
                className={className || "button-3d rounded-lg bg-[#ffd372] text-charcoal hover:bg-[#ffe58a] hover:text-black delay-200 ease-initial font-winky text-sm px-4 py-2 border-1 border-[#4c4c4c] transition-all duration-200 transform hover:scale-105 active:scale-95 active:translate-y-[2px] shadow-[2px_2px_0_#4c4c4c] flex items-center gap-1"}
            >
                <Download className="w-4 h-4" />
                Install
            </Button>

            {/* Installation Instructions Modal */}
            {showModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
                    <div className="relative w-full max-w-md rounded-3xl border-2 border-[#4c4c4c] bg-[#fffdfa] p-8 shadow-[6px_6px_0_#4c4c4c] text-center">
                        <button
                            type="button"
                            onClick={handleDismissModal}
                            className="absolute right-4 top-4 rounded-full border border-[#4c4c4c]/30 bg-white/70 p-1 text-[#4c4c4c] transition-colors hover:bg-white"
                            aria-label="Close"
                        >
                            <svg
                                className="h-4 w-4"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>

                        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border-2 border-[#4c4c4c] bg-[#ffd372]/50">
                            <img src="/logo.png" alt="Hoku" className="h-10 w-10" />
                        </div>

                        <h2 className="font-winky text-2xl font-semibold text-[#4c4c4c]">
                            Install Hey Hoku
                        </h2>

                        {isIOS ? (
                            <div className="mt-4 text-left space-y-3">
                                <p className="text-[#4c4c4c]/80">
                                    To install on your iPhone/iPad:
                                </p>
                                <ol className="list-decimal list-inside space-y-2 text-[#4c4c4c]/80">
                                    <li>Tap the <strong>Share</strong> button <span className="inline-block">⬆️</span> in Safari</li>
                                    <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
                                    <li>Tap <strong>"Add"</strong> in the top right</li>
                                </ol>
                            </div>
                        ) : (
                            <div className="mt-4 text-left space-y-3">
                                <p className="text-[#4c4c4c]/80">
                                    To install Hey Hoku:
                                </p>
                                <ol className="list-decimal list-inside space-y-2 text-[#4c4c4c]/80">
                                    <li>Click the <strong>install icon</strong> in your browser's address bar</li>
                                    <li>Or open browser menu (⋮) and select <strong>"Install app"</strong></li>
                                </ol>
                            </div>
                        )}

                        <div className="mt-8">
                            <Button
                                type="button"
                                onClick={handleDismissModal}
                                className="h-12 w-full rounded-lg border-2 border-[#4c4c4c] bg-[#ffd372] font-winky text-base text-[#4c4c4c] transition-all duration-200 ease-in-out hover:bg-[#ffe58a] hover:scale-105 hover:-translate-y-[2px] active:scale-95 active:translate-y-[2px] shadow-[3px_3px_0_#4c4c4c]"
                            >
                                Got it!
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
